import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { UserGroupIcon, PlusIcon } from "@heroicons/react/solid";

import { getAllUserGroups } from "../../../../utils/api";
import useMountStatus from "../../../../hooks/useMountStatus";
import { NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";
import { DashboardContext } from "../../../../context/DashboardContext";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import AccessControl from "../../../SharedComponents/AccessControl";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import UserGroupRow from "./UserGroupRow";
import PaginatedSearch from "../../../SharedComponents/PaginatedSearch";

function UserGroups() {
    const { searchTerm } = useParams();

    const [userGroups, setUserGroups] = useState([]);
    const [userGroupsLoading, setUserGroupsLoading] = useState(true);
    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const unmounted = useMountStatus();

    const navigate = useNavigate();

    const location = useLocation();

    const context = useContext(DashboardContext);

    const fetchUserGroups = useCallback(async () => {
        setUserGroupsLoading(true);
        try {
            const userGroupsResponse = await getAllUserGroups();
            if (unmounted) return;
            setUserGroups(userGroupsResponse);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setUserGroupsLoading(false);
    }, [unmounted]);

    const handleGoToAddUserGroup = () => {
        navigate("/user-groups/add-user-group");
    };

    const handleSetDeleteAlert = useCallback(() => {
        if (unmounted) return;
        setAlertType(ALERT_TYPE.SUCCESS);
        setAlert(location.state.successfulDelete);
    }, [location.state, unmounted]);

    useEffect(() => {
        fetchUserGroups();
        location.state && handleSetDeleteAlert();
    }, [fetchUserGroups, handleSetDeleteAlert, location.state]);

    return (
        <AccessControl requiredPermission={PERMISSIONS.VIEW_USER_GROUPS_LIST} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer
                title={"User Groups"}
                titleIcon={<UserGroupIcon className="block h-5 w-5 mr-2" />}
                actionButton
                actionButtonText={"Add User Group"}
                actionButtonIcon={<PlusIcon className="block h-4 w-4 mr-2" />}
                actionButtonOnClick={handleGoToAddUserGroup}
                actionButtonPermission={PERMISSIONS.CREATE_USER_GROUPS}
            >
                <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                <PaginatedSearch
                    loading={userGroupsLoading}
                    items={userGroups}
                    itemName={"User groups"}
                    searchAttributes={["name", "parentGroupName", "userGroupName", "users"]}
                    columnNames={
                        context.userPermissions.includes(PERMISSIONS.EDIT_USER_GROUPS)
                            ? ["Name", "Parent group", "Users", "Permissions", "Applications", "Edit"]
                            : ["Name", "Parent group", "Users", "Permissions", "Applications", "Edit"]
                    }
                    ItemRow={UserGroupRow}
                    customItemsPerPage={16}
                    initialSearchValue={searchTerm}
                />
            </DashboardContainer>
        </AccessControl>
    );
}

export default UserGroups;
